import { defineAsyncComponent } from 'vue'
import { createRouter, createWebHistory } from 'vue-router'

import { useAuthStore } from '@/stores/auth.store'
import { MFA_STAGES, CognitoGroups } from '@/stores/auth.constants'

// Vite restricts variable dynamic imports to one directory level, so the lazy
// helpers resolve through import.meta.glob maps (path -> lazy loader) instead.
const layoutModules = import.meta.glob('../layouts/*.vue')
const viewModules = import.meta.glob('../views/**/*.vue')
// Only the form folders are routed; a broad components glob would pull the
// globally registered commons into ineffective dynamic imports.
const componentModules = import.meta.glob([
  '../components/creation-forms/*.vue',
  '../components/editing-forms/*.vue'
])

// Layouts consumed via <component :is> in App.vue (meta.layout) must be resolved
// async components, not raw factories (Vue 3 renders a bare factory as [object
// Promise]). Layouts used as a route `component` must instead be plain lazy
// factories, or vue-router warns about defineAsyncComponent.
const lazyLayout = (layout) => defineAsyncComponent(layoutModules[`../layouts/${layout}.vue`])
const lazyLayoutRoute = (layout) => layoutModules[`../layouts/${layout}.vue`]
const lazyView = (view) => viewModules[`../views/${view}.vue`]
const lazyComponent = (component) => componentModules[`../components/${component}.vue`]

const LayoutDefault = lazyLayoutRoute('LayoutDefault')
const Dashboard = lazyView('Dashboard')
const PageNotFound = lazyView('PageNotFound')
const Categories = lazyView('Categories')
const TrafficSources = lazyView('TrafficSources')
const ParentCategories = lazyView('ParentCategories')
const Countries = lazyView('Countries')
const Languages = lazyView('Languages')
const RegistrationRequests = lazyView('RegistrationRequests/RegistrationRequests')
const RegistrationRequestDetails = lazyView('RegistrationRequests/RegistrationRequestDetails')
const ReadyForOnboarding = lazyView('RegistrationRequests/ReadyForOnboarding')
const SellerOfferPendingReviewRequests = lazyView('SellerOffers/PendingRegistrations')
const SellerOfferPendingReviewRequestDetails = lazyView('SellerOffers/PendingRegistrationDetails')
const SellerOfferApprovedRegistrationDetails = lazyView('SellerOffers/SellerOfferApprovedRegistrationDetails')

const SellerOffers = lazyView('SellerOffers')
const SellerOfferCreate = lazyComponent('creation-forms/SellerOfferCreate')
const SellerOfferEdit = lazyComponent('editing-forms/SellerOfferEdit')
const SellerOfferInsightsPage = lazyView('SellerOffers/SellerOfferInsightsPage')

const KYCRequests = lazyView('KYCRequests/KYCRequests')
const KYCRequestDetails = lazyView('KYCRequests/KYCRequestDetails')
const CategoryCreate = lazyComponent('creation-forms/CategoryCreate')
const LanguageCreate = lazyComponent('creation-forms/LanguageCreate')
const TrafficSourceCreate = lazyComponent('creation-forms/TrafficSourceCreate')
const CountryCreate = lazyComponent('creation-forms/CountryCreate')
const ParentCategoryCreate = lazyComponent('creation-forms/ParentCategoryCreate')
const SuperAdminUsers = lazyView('Users/SuperAdminUsers')
const SuperAdminUserCreate = lazyView('Users/SuperAdminUserCreate')
const AccountManagers = lazyView('Users/AccountManagers')
const SellerAccounts = lazyView('Accounts/SellerAccounts')
const BuyerAccounts = lazyView('Accounts/BuyerAccounts')
const BuyerAccountDetails = lazyView('Accounts/BuyerAccountDetails')
const BuyerAccountAddFunds = lazyView('Accounts/BuyerAccountAddFunds')
const SellerAccountAddFunds = lazyView('Accounts/SellerAccountAddFunds')
const SellerAccountDetails = lazyView('Accounts/SellerAccountDetails')

const BuyerPaymentsList = lazyView('Billing/BuyerPaymentsList')
const SellerPaymentsList = lazyView('Billing/SellerPaymentsList')
const SellersPaymentHistory = lazyView('Billing/SellersPaymentHistory')
const BuyersPaymentHistory = lazyView('Billing/BuyersPaymentHistory')
const AccountPaymentDetails = lazyView('Billing/AccountPaymentDetails')
const AdvancePaymentDetails = lazyView('Billing/AdvancePaymentDetails')
const AdvancePaymentsList = lazyView('Billing/AdvancePaymentsList')
const AccountSummary = lazyView('Billing/AccountSummary')
const CreditCardCharges = lazyView('Billing/CreditCardCharges')
const CategorySupplyDemand = lazyView('Categories/CategorySupplyDemand')
const ExchangeAnalytics = lazyView('ExchangeAnalytics/ExchangeAnalytics')
const ExchangeRejectionInsights = lazyView('ExchangeAnalytics/RejectionInsights')
const ExchangePingHealth = lazyView('ExchangeAnalytics/PingHealth')
const ExchangeSellerBenchmarks = lazyView('ExchangeAnalytics/SellerBenchmarks')
const ExchangeDemandMap = lazyView('ExchangeAnalytics/DemandMap')

const CategoryEdit = lazyComponent('editing-forms/CategoryEdit')
const ParentCategoryEdit = lazyComponent('editing-forms/ParentCategoryEdit')
const CountryEdit = lazyComponent('editing-forms/CountryEdit')
const LanguageEdit = lazyComponent('editing-forms/LanguageEdit')
const TrafficSourceEdit = lazyComponent('editing-forms/TrafficSourceEdit')

const LayoutAuth = lazyLayout('LayoutAuth')
const Login = lazyView('Login/Login')
const LoginEmail = lazyView('Login/LoginEmail')
const PasswordReset = lazyView('Login/PasswordReset')
const SetNewPassword = lazyView('Login/SetNewPassword')
const LoginChooseMFAType = lazyView('Login/LoginChooseMFAType')
const LoginMFA = lazyView('Login/LoginMFA')
const LoginEnterPermanentPassword = lazyView('Login/LoginEnterPermanentPassword')

const BuyerCampaigns = lazyView('Campaigns/buyer/BuyerCampaigns')
const PendingApprovalBuyerCampaigns = lazyView('Campaigns/buyer/PendingApprovalBuyerCampaigns')
const BuyerCampaignDetails = lazyView('Campaigns/buyer/BuyerCampaignDetails')

const TwoFactorAuth = lazyView('Auth/TwoFactorAuth')

const RegistrationChecklistItemCreate = lazyView('RegistrationChecklistItem/RegistrationChecklistItemCreate')
const RegistrationChecklistItemEdit = lazyView('RegistrationChecklistItem/RegistrationChecklistItemEdit')
const RegistrationChecklistItems = lazyView('RegistrationChecklistItem/RegistrationChecklistItems')

const GlobalSettings = lazyView('Settings/GlobalSettings')
const ChannelNameSearch = lazyView('Tools/ChannelNameSearch')
const SellerAccountPaymentHistory = lazyView('Billing/SellerAccountPaymentHistory')
const BuyerAccountPaymentHistory = lazyView('Billing/BuyerAccountPaymentHistory')
const SellerPayoutMetrics = lazyView('Billing/SellerPayoutMetrics')
const ScreeningPendingCreatives = lazyView('Creatives/ScreeningPendingCreatives')
const ScreenCreative = lazyView('Creatives/ScreenCreative')
const Creatives = lazyView('Creatives/Creatives')
const CreativeDetails = lazyView('Creatives/CreativeDetails')
const CallLogs = lazyView('Calls/CallLogs')
const DisputesManualAdjustments = lazyView('Disputes/DisputesManualAdjustments')
const DisputesHistory = lazyView('Disputes/DisputesHistory')
const IntegrationSyncChecks = lazyView('Tools/IntegrationSyncChecks')
const Frauds = lazyView('Frauds/Frauds')
const FraudDetails = lazyView('Frauds/FraudDetails')

function checkIfRouteHasGroup(isAuthenticated, to) {
  if (isAuthenticated && to.meta.accessUserGroups?.length) {
    const userData = useAuthStore().userDetails

    return checkUserGroup(userData?.groups, to.meta.accessUserGroups)
  }

  return true
}

function checkUserGroup(userGroups, allowedAccessGroup) {
  return userGroups?.some(r=> allowedAccessGroup.includes(r))
}


const routes = [
  {
    path: '/login',
    component: Login,
    meta: {
      requiresAuth: false,
      redirectIfAlreadyLoggedIn: true,
      layout: LayoutAuth
    },
    children: [
      {
        path: '',
        name: 'LoginEmail',
        component: LoginEmail
      },
      {
        path: 'forgot-password',
        name: 'PasswordReset',
        component: PasswordReset
      },
      {
        path: 'set-new-password',
        name: 'SetNewPassword',
        component: SetNewPassword,
        props: route => ({ email: route.query.email })
      },
      {
        path: 'choose-mfa',
        name: 'LoginChooseMFAType',
        component: LoginChooseMFAType,
        beforeEnter(to, from, next) {
          const { currentAuthChallenge } = useAuthStore()
          if (currentAuthChallenge !== MFA_STAGES.SELECT_MFA_TYPE) {
            next({
              name: 'LoginEmail'
            })
          } else {
            next()
          }
        }
      },
      {
        path: 'enter-mfa',
        name: 'LoginMFA',
        component: LoginMFA,
        beforeEnter(to, from, next) {
          const { currentAuthChallenge } = useAuthStore()
          if (currentAuthChallenge !== MFA_STAGES.SMS_MFA && currentAuthChallenge !== MFA_STAGES.SOFTWARE_TOKEN_MFA) {
            next({
              name: 'LoginEmail'
            })
          } else {
            next()
          }
        }
      },
      {
        path: 'enter-permanent-password',
        name: 'LoginEnterPermanentPassword',
        component: LoginEnterPermanentPassword,
        beforeEnter(to, from, next) {
          const { currentAuthChallenge } = useAuthStore()

          if (currentAuthChallenge !== MFA_STAGES.NEW_PASSWORD_REQUIRED) {
            next({
              name: 'LoginEmail'
            })
          } else {
            next()
          }
        }
      }
    ]
  },
  {
    path: '/',
    name: 'Home',
    redirect: '/dashboard',
    meta: {
      requiresAuth: true
    },
  },
  {
    path: '/dashboard',
    component: LayoutDefault,
    meta: {
      requiresAuth: true,
    },
    beforeEnter: async (to, from, next) => {
      checkIsMFASetup(next, to)
    },
    children: [
      {
        path: '',
        name: 'Dashboard',
        component: Dashboard
      },
      {
        path: 'languages/:id/edit',
        name: 'LanguageEdit',
        component: LanguageEdit
      },
      {
        path: 'languages/create',
        name: 'LanguageCreate',
        component: LanguageCreate
      },
      {
        path: 'traffic-sources/:id/edit',
        name: 'TrafficSourceEdit',
        component: TrafficSourceEdit
      },
      {
        path: 'traffic-sources/create',
        name: 'TrafficSourceCreate',
        component: TrafficSourceCreate
      },
      {
        path: 'countries/:id/edit',
        name: 'CountryEdit',
        component: CountryEdit
      },
      {
        path: 'countries/create',
        name: 'CountryCreate',
        component: CountryCreate
      },
      {
        path: 'parent-categories/:id/edit',
        name: 'ParentCategoryEdit',
        component: ParentCategoryEdit
      },
      {
        path: 'mfa-setup',
        name: 'TwoFactorAuth',
        component: TwoFactorAuth
      },
      {
        path: 'parent-categories/create',
        name: 'ParentCategoryCreate',
        component: ParentCategoryCreate
      },
      {
        path: 'parent-categories',
        name: 'ParentCategories',
        component: ParentCategories
      },
      {
        path: 'categories/:id/edit',
        name: 'CategoryEdit',
        component: CategoryEdit
      },
      {
        path: 'categories/supply-demand',
        name: 'CategorySupplyDemand',
        component: CategorySupplyDemand,
      },
      {
        path: 'exchange-analytics',
        name: 'ExchangeAnalytics',
        component: ExchangeAnalytics,
        meta: {
          // the backend endpoint is SUPER_ADMIN_ONLY
          accessUserGroups: [CognitoGroups.ADMIN]
        }
      },
      {
        path: 'exchange-analytics/rejections',
        name: 'ExchangeRejectionInsights',
        component: ExchangeRejectionInsights,
        meta: {
          // the backend endpoint is SUPER_ADMIN_ONLY
          accessUserGroups: [CognitoGroups.ADMIN]
        }
      },
      {
        path: 'exchange-analytics/ping-health',
        name: 'ExchangePingHealth',
        component: ExchangePingHealth,
        meta: {
          // the backend endpoint is SUPER_ADMIN_ONLY
          accessUserGroups: [CognitoGroups.ADMIN]
        }
      },
      {
        // Market Intelligence merged into the Overview page; the function form keeps the
        // query (segment/date/target deep links) across the redirect, which a bare named
        // redirect would drop
        path: 'exchange-analytics/market-intel',
        redirect: to => ({ name: 'ExchangeAnalytics', query: to.query })
      },
      {
        path: 'exchange-analytics/sellers',
        name: 'ExchangeSellerBenchmarks',
        component: ExchangeSellerBenchmarks,
        meta: {
          // the backend endpoint is SUPER_ADMIN_ONLY
          accessUserGroups: [CognitoGroups.ADMIN]
        }
      },
      {
        path: 'exchange-analytics/demand-map',
        name: 'ExchangeDemandMap',
        component: ExchangeDemandMap,
        meta: {
          // the backend endpoint is SUPER_ADMIN_ONLY
          accessUserGroups: [CognitoGroups.ADMIN]
        }
      },
      {
        path: 'categories/create',
        name: 'CategoryCreate',
        component: CategoryCreate
      },
      {
        path: 'categories',
        name: 'Categories',
        component: Categories
      },
      {
        path: 'traffic-sources',
        name: 'TrafficSources',
        component: TrafficSources
      },
      {
        path: 'seller-offers',
        name: 'Seller Offers',
        component: SellerOffers
      },
      {
        path: 'seller-offers/:id/insights',
        name: 'SellerOfferInsights',
        component: SellerOfferInsightsPage
      },
      {
        path: 'seller-offers/:id/edit',
        name: 'SellerOfferEdit',
        component: SellerOfferEdit
      },
      {
        path: 'seller-offers/:id/registration/:registrationId',
        name: 'SellerOfferRegistrationEdit',
        component: SellerOfferApprovedRegistrationDetails
      },
      {
        path: 'seller-offer-registrations/:registrationId',
        name: 'SellerOfferRegistrationDetails',
        component: SellerOfferApprovedRegistrationDetails
      },
      {
        path: 'seller-offers/create',
        name: 'SellerOfferCreate',
        component: SellerOfferCreate
      },
      {
        path: 'countries',
        name: 'Countries',
        component: Countries
      },
      {
        path: 'languages',
        name: 'Languages',
        component: Languages
      },

      {
        path: 'seller-offers/review',
        name: 'SellerOfferPendingReviewRequests',
        component: SellerOfferPendingReviewRequests
      },

      {
        path: 'seller-offers/review/:id',
        name: 'SellerOfferPendingReviewRequestDetails',
        component: SellerOfferPendingReviewRequestDetails
      },

      {
        path: 'buyer-campaigns/review',
        name: 'PendingApprovalBuyerCampaigns',
        component: PendingApprovalBuyerCampaigns
      },

      {
        path: 'registration-requests/ready-for-onboarding',
        name: 'ReadyForOnboarding',
        component: ReadyForOnboarding
      },

      {
        path: 'registration-requests/:type',
        name: 'RegistrationRequests',
        component: RegistrationRequests
      },

      {
        path: 'registration-requests/:type/:id',
        name: 'RegistrationRequestDetails',
        component: RegistrationRequestDetails
      },

      {
        path: 'kyc-requests',
        name: 'KYCRequests',
        component: KYCRequests
      },

      {
        path: 'kyc-requests/:id',
        name: 'KYCRequestDetails',
        component: KYCRequestDetails
      },

      {
        path: 'super-admin-users',
        name: 'SuperAdminUsers',
        component: SuperAdminUsers
      },

      {
        path: 'super-admin-users/create',
        name: 'SuperAdminUserCreate',
        component: SuperAdminUserCreate
      },

      {
        path: 'account-managers',
        name: 'AccountManagers',
        component: AccountManagers
      },

      {
        path: 'accounts/buyer',
        name: 'BuyerAccounts',
        component: BuyerAccounts
      },

      {
        path: 'accounts/seller',
        name: 'SellerAccounts',
        component: SellerAccounts
      },

      {
        path: 'billing/buyer',
        name: 'BuyerPaymentsList',
        component: BuyerPaymentsList
      },

      {
        path: 'billing/seller',
        name: 'SellerPaymentsList',
        component: SellerPaymentsList
      },

      {
        path: 'billing/seller/paid',
        name: 'SellersPaymentHistory',
        component: SellersPaymentHistory
      },
      {
        path: 'billing/buyer/paid',
        name: 'BuyersPaymentHistory',
        component: BuyersPaymentHistory
      },

      {
        path: 'billing/advance-pay',
        name: 'AdvancePaymentsList',
        component: AdvancePaymentsList
      },

      {
        path: 'billing/payment/:accountId/:invoiceId',
        name: 'AccountPaymentDetails',
        component: AccountPaymentDetails
      },

      {
        path: 'billing/payment/:accountId/:advancePaymentId',
        name: 'AdvancePaymentDetails',
        component: AdvancePaymentDetails
      },

      {
        path: 'billing/account/summary',
        name: 'AccountSummary',
        component: AccountSummary
      },

      {
        path: 'billing/credit-card-charges',
        name: 'CreditCardCharges',
        component: CreditCardCharges
      },

      {
        path: 'accounts/buyer/:id',
        name: 'BuyerAccountDetails',
        component: BuyerAccountDetails
      },

      {
        path: 'accounts/buyer/add-funds/:accountId',
        name: 'BuyerAccountAddFunds',
        component: BuyerAccountAddFunds,
        props: true,
        meta: {
          accessUserGroups: [CognitoGroups.FINANCIAL_MANAGER, CognitoGroups.ADMIN]
        },
      },

      {
        path: 'accounts/seller/add-funds/:accountId',
        name: 'SellerAccountAddFunds',
        component: SellerAccountAddFunds,
        props: true,
        meta: {
          accessUserGroups: [CognitoGroups.FINANCIAL_MANAGER, CognitoGroups.ADMIN]
        },
      },

      {
        path: 'accounts/seller/:id',
        name: 'SellerAccountDetails',
        component: SellerAccountDetails
      },

      {
        path: 'accounts/seller/:id/payments',
        name: 'SellerAccountPaymentHistory',
        component: SellerAccountPaymentHistory
      },
      {
        path: 'accounts/seller/:id/payout-metrics',
        name: 'SellerPayoutMetrics',
        component: SellerPayoutMetrics
      },
      {
        path: 'accounts/buyer/:id/payments',
        name: 'BuyerAccountPaymentHistory',
        component: BuyerAccountPaymentHistory
      },

      {
        path: 'campaigns/buyer',
        name: 'BuyerCampaigns',
        component: BuyerCampaigns
      },

      {
        path: 'campaigns/buyer/:id',
        name: 'BuyerCampaignDetails',
        component: BuyerCampaignDetails
      },

      {
        path: 'registration-checklist-items/:type',
        name: 'RegistrationChecklistItems',
        component: RegistrationChecklistItems,
      },

      {
        path: 'registration-checklist-item/:type/create',
        name: 'RegistrationChecklistItemCreate',
        component: RegistrationChecklistItemCreate,
      },

      {
        path: 'registration-checklist-item/:type/edit/:id',
        name: 'RegistrationChecklistItemEdit',
        component: RegistrationChecklistItemEdit,
      },

      {
        path: 'channel-name-search',
        name: 'ChannelNameSearch',
        component: ChannelNameSearch
      },

      {
        path: 'calls',
        name: 'Reporting',
        component: CallLogs
      },

      {
        path: 'adjust-dispute-calls',
        name: 'DisputesManualAdjustments',
        component: DisputesManualAdjustments,
        props: route => ({ callIdsCommaSeparated: route.query.callids, dateRangeCommaSeparated: route.query.daterange })
      },

      {
        path: 'disputes-history',
        name: 'DisputesHistory',
        component: DisputesHistory
      },

      {
        path: 'frauds',
        name: 'Frauds',
        component: Frauds
      },

      {
        path: 'frauds/:id',
        name: 'FraudDetails',
        component: FraudDetails
      },

      {
        path: 'integration-sync-checks',
        name: 'IntegrationSyncChecks',
        component: IntegrationSyncChecks
      },

      {
        path: 'global-settings',
        name: 'GlobalSettings',
        component: GlobalSettings
      },

      {
        path: 'creatives/review',
        name: 'ScreeningPendingCreatives',
        component: ScreeningPendingCreatives
      },

      {
        path: 'creatives/review/:id',
        name: 'ScreenCreative',
        component: ScreenCreative
      },
      {
        path: 'creatives',
        name: 'Creatives',
        component: Creatives
      },
      {
        path: 'creatives/:id',
        name: 'CreativeDetails',
        component: CreativeDetails
      },

    ]
  },

  { path: '/:pathMatch(.*)*', name: 'NotFound', component: PageNotFound }
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes
})

function checkIsMFASetup(next, to) {
  const { isMFASetup } = useAuthStore()

  if (!isMFASetup && to.name !== 'TwoFactorAuth' && to.matched.some((record) => record.meta.requiresAuth)) {
    next({
      name: 'TwoFactorAuth'
    })
  } else {
    next()
  }
}

router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()
  const isAuthenticated = authStore.isAuthenticated
  let haveUserData = authStore.haveUserData

  if (isAuthenticated && !haveUserData) {
    await authStore.getLoggedInUserDetails()
    haveUserData = authStore.haveUserData
  }

  if (isAuthenticated && haveUserData) {
    await authStore.startPollingForNewAccessToken()
  }

  if (to.matched.some((record) => record.meta.requiresAuth) && !isAuthenticated) {
    // this route requires auth, check if logged in
    // if not, redirect to login page.
    next({
      name: 'LoginEmail',
      query: { redirect: to.fullPath },
    })
  } else if (to.matched.some((record) => record.meta.redirectIfAlreadyLoggedIn) && isAuthenticated) {
    next({
      name: 'Dashboard'
    })
  } else {
    if (!checkIfRouteHasGroup(isAuthenticated, to)) {
      next({
        name: 'Dashboard'
      })
      return
    }

    checkIsMFASetup(next, to)
  }
})

export default router
